import React, {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react"
import { cn } from "~/lib/utils"
import { Icon } from "~/react/ui/icon"
import { Button } from "~/react/ui/button"
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "~/react/ui/tooltip"

const t = (key: string) => window.i18n.t(key)

/**
 * PROACTIVE AI PROMPT — Autosizing, responsive input component
 * -------------------------------------------------------------
 * General Overview:
 * A minimal prompt field that dynamically expands from a single line into
 * multiline as users type more text. Designed for quick input and fast
 * submission in AI or chat-driven interfaces.
 *
 * Autosizing & Layout:
 * The component measures its single-line height once, then automatically
 * adjusts height to fit content as users type. When the input becomes long
 * enough (wraps or includes newlines), it expands; when short again, it
 * collapses back below a set threshold.
 *
 * Positioning & Animation:
 * Supports both center and small modes. Uses lightweight 'out' and 'in'
 * transition flags so CSS can handle animations without JS dependencies.
 *
 * Imperative API:
 * Exposes animateToSmall() and animateToCenter() methods through a ref for
 * parent components that want to reposition the prompt programmatically.
 */

// -----------------------------
// Constants
// -----------------------------
const COLLAPSE_THRESHOLD = 65
const WRAP_EPSILON = 2
const LOADING_DURATION = 30000
const TRANSITION_OUT_MS = 150
const TRANSITION_IN_TICK_MS = 10

// -----------------------------
// Types
// -----------------------------
export interface ProactiveAiPromptProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onSubmit" | "isSmall"> {
  placeholder?: string
  onSubmit?: (value: string) => void
  /**
   * Initial layout mode:
   * - false (default): center/large variant
   * - true: small/inline variant
   */
  isSmall?: boolean
}

export interface ProactiveAiPromptHandle {
  animateToSmall: () => void
  animateToCenter: () => void
}

// -----------------------------
// Hooks
// -----------------------------
/**
 * useAutosizeTextarea
 *  - Measures single-line height once
 *  - Expands textarea height based on content
 */
function useAutosizeTextarea(
  ref: React.RefObject<HTMLTextAreaElement | null>,
  value: string,
) {
  const singleLineHeightRef = useRef(0)

  const measureSingleLineHeight = useCallback(() => {
    const element = ref.current
    if (!element) return 0
    const previousValue = element.value
    const previousHeight = element.style.height

    element.value = "x"
    element.style.height = "auto"
    const height = element.scrollHeight

    element.value = previousValue
    element.style.height = previousHeight
    return height
  }, [ref])

  const recalc = useCallback(() => {
    const element = ref.current
    if (!element) return
    element.style.height = "auto"
    // Force reflow to make next measurement reliable
    void element.offsetHeight
    element.style.height = `${Math.max(element.scrollHeight, singleLineHeightRef.current)}px`
  }, [ref])

  const isWrapped = useCallback(() => {
    const element = ref.current
    if (!element) return false
    return element.scrollHeight > singleLineHeightRef.current + WRAP_EPSILON
  }, [ref])

  const remeasure = useCallback(() => {
    singleLineHeightRef.current = measureSingleLineHeight()
    recalc()
  }, [measureSingleLineHeight, recalc])

  useLayoutEffect(() => {
    remeasure()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useLayoutEffect(() => {
    recalc()
  }, [value, recalc])

  return { recalc, isWrapped, remeasure, singleLineHeight: singleLineHeightRef }
}

/**
 * useMultilineLayout
 *  - Determines whether the field should switch to multiline layout
 *  - Applies center/small specific thresholds
 */
function useMultilineLayout(
  value: string,
  isSmall: boolean,
  isWrapped: () => boolean,
  recalc: () => void,
) {
  const [isMultiline, setIsMultiline] = useState(false)

  const update = useCallback(() => {
    // In small mode, always multiline
    if (isSmall) {
      if (!isMultiline) {
        setIsMultiline(true)
        requestAnimationFrame(recalc)
      }
      return
    }

    // Never multiline when empty (in center mode)
    if (value.length === 0) {
      if (isMultiline) {
        setIsMultiline(false)
        requestAnimationFrame(recalc)
      }
      return
    }

    const threshold = COLLAPSE_THRESHOLD
    const hasNewline = value.includes("\n")
    const wrapped = isWrapped()
    const shouldBeMultiline = hasNewline || wrapped

    // Transition rules are intentionally asymmetric: once multiline,
    // collapse back only when content is short enough.
    if (shouldBeMultiline && !isMultiline) {
      setIsMultiline(true)
      // defer so className can flip before we measure
      requestAnimationFrame(recalc)
    } else if (!shouldBeMultiline && isMultiline && value.length < threshold) {
      setIsMultiline(false)
      requestAnimationFrame(recalc)
    }
  }, [isSmall, isMultiline, isWrapped, recalc, value])

  useEffect(() => {
    update()
  }, [value, update])

  return isMultiline
}

/**
 * usePromptTransitions
 *  - Manages small/center position with lightweight entry/exit flags
 *  - Keeps timings centralized and easy to tweak
 *  - Cleans up timers to avoid leaks
 */
function usePromptTransitions(initialIsSmall: boolean) {
  const [isSmall, setIsSmall] = useState(initialIsSmall)
  const [isTransitioningOut, setTransitioningOut] = useState(false)
  const [isTransitioningIn, setTransitioningIn] = useState(false)

  const outTimeoutId = useRef<number | null>(null)
  const inTimeoutId = useRef<number | null>(null)

  const animatePosition = useCallback((target: "small" | "center") => {
    const targetIsSmall = target === "small"
    setTransitioningOut(true)
    // Clear any existing timers
    if (outTimeoutId.current) window.clearTimeout(outTimeoutId.current)
    if (inTimeoutId.current) window.clearTimeout(inTimeoutId.current)

    outTimeoutId.current = window.setTimeout(() => {
      setIsSmall(targetIsSmall)
      setTransitioningOut(false)
      setTransitioningIn(true)
      inTimeoutId.current = window.setTimeout(
        () => setTransitioningIn(false),
        TRANSITION_IN_TICK_MS,
      )
    }, TRANSITION_OUT_MS)
  }, [])

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (outTimeoutId.current) window.clearTimeout(outTimeoutId.current)
      if (inTimeoutId.current) window.clearTimeout(inTimeoutId.current)
    }
  }, [])

  return { isSmall, isTransitioningOut, isTransitioningIn, animatePosition }
}

// -----------------------------
// Component
// -----------------------------
const ProactiveAiPrompt = forwardRef<
  ProactiveAiPromptHandle,
  ProactiveAiPromptProps
>(
  (
    {
      placeholder,
      onSubmit,
      className,
      isSmall: isSmallProp = false,
      ...props
    },
    ref,
  ) => {
    const defaultPlaceholder = t("js.proactive_ai_prompt.placeholder.large")
    const smallPlaceholder = t("js.proactive_ai_prompt.placeholder.small")
    // -----------------------------
    // State
    // -----------------------------
    const [value, setValue] = useState("")
    const [isLoading, setIsLoading] = useState(false)

    // -----------------------------
    // Refs & IDs
    // -----------------------------
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const loadingTimeoutId = useRef<number | null>(null)
    const prevIsSmallRef = useRef(false)
    const wasTransitioningRef = useRef(false)
    const focusTimeoutId = useRef<number | null>(null)
    const instructionsId = useId()
    const inputId = useId()

    // -----------------------------
    // Custom Hooks
    // -----------------------------
    const { recalc, isWrapped, remeasure } = useAutosizeTextarea(
      textareaRef,
      value,
    )
    const { isSmall, isTransitioningOut, isTransitioningIn, animatePosition } =
      usePromptTransitions(isSmallProp)
    const isMultiline = useMultilineLayout(value, isSmall, isWrapped, recalc)

    // -----------------------------
    // Memoized Values
    // -----------------------------
    const effectivePlaceholder = placeholder ?? defaultPlaceholder
    const trimmedPlaceholder = useMemo(
      () => effectivePlaceholder.trim(),
      [effectivePlaceholder],
    )
    const isDisabled = useMemo(
      () => isLoading || value.trim() === "",
      [isLoading, value],
    )

    // -----------------------------
    // Imperative Handle
    // -----------------------------
    useImperativeHandle(
      ref,
      () => ({
        animateToSmall: () => animatePosition("small"),
        animateToCenter: () => animatePosition("center"),
      }),
      [animatePosition],
    )

    // -----------------------------
    // Effects
    // -----------------------------
    // Recalculate height when mode changes (width changes affect single-line height)
    useEffect(() => {
      const wasSmall = prevIsSmallRef.current
      const nowSmall = isSmall

      if (!wasSmall && nowSmall) {
        // Transitioning TO small: wait for transition to complete, then remeasure
        if (!isTransitioningOut && !isTransitioningIn) {
          remeasure()
        }
      } else if (wasSmall && !nowSmall) {
        // Transitioning back to center: remeasure immediately (width changed)
        remeasure()
      }

      prevIsSmallRef.current = isSmall
    }, [isSmall, isTransitioningOut, isTransitioningIn, remeasure])

    // Focus whenever transition completes (watch for transition flags becoming false)
    useEffect(() => {
      const isTransitioning = isTransitioningOut || isTransitioningIn
      const wasTransitioning = wasTransitioningRef.current

      // If we were transitioning and now we're not, focus the textarea
      if (wasTransitioning && !isTransitioning) {
        // Clear any existing focus timeout
        if (focusTimeoutId.current) window.clearTimeout(focusTimeoutId.current)
        // Small delay to ensure DOM is fully updated
        focusTimeoutId.current = window.setTimeout(() => {
          textareaRef.current?.focus()
        }, 20)
      }

      wasTransitioningRef.current = isTransitioning
    }, [isTransitioningOut, isTransitioningIn])

    // Cleanup effects
    useEffect(() => {
      return () => {
        if (loadingTimeoutId.current)
          window.clearTimeout(loadingTimeoutId.current)
        if (focusTimeoutId.current) window.clearTimeout(focusTimeoutId.current)
      }
    }, [])

    // -----------------------------
    // Event Handlers
    // -----------------------------
    const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
      setValue(e.target.value)

    const handleSubmit = useCallback(() => {
      if (isDisabled) return
      onSubmit?.(value)
      setValue("")
      setIsLoading(true)
      // Clear any existing loading timer
      if (loadingTimeoutId.current)
        window.clearTimeout(loadingTimeoutId.current)
      loadingTimeoutId.current = window.setTimeout(
        () => setIsLoading(false),
        LOADING_DURATION,
      )
      // Keep focus on the field for speed
      requestAnimationFrame(() => textareaRef.current?.focus())
    }, [isDisabled, onSubmit, value])

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key !== "Enter") return
      if (e.shiftKey && value.trim() !== "") {
        // Allow newline only if field is not empty
        return
      }
      e.preventDefault()
      if (!isDisabled) handleSubmit()
    }

    const handleContainerClick = (e: React.MouseEvent) => {
      if (!(e.target as HTMLElement).closest(".proactive-ai-prompt__button")) {
        textareaRef.current?.focus()
      }
    }

    return (
      <TooltipProvider>
        <div
          className={cn(
            "proactive-ai-prompt",
            isSmall && "proactive-ai-prompt--small",
            isTransitioningOut && "proactive-ai-prompt--transitioning-out",
            isTransitioningIn && "proactive-ai-prompt--transitioning-in",
            className,
          )}
          onClick={handleContainerClick}
          {...props}
        >
          <form
            className={cn(
              "proactive-ai-prompt__field",
              isMultiline && "proactive-ai-prompt__field--multiline",
            )}
            role="search"
            aria-label={trimmedPlaceholder}
            aria-busy={isLoading}
            onSubmit={(e) => {
              e.preventDefault()
              handleSubmit()
            }}
          >
            <Icon
              icon="sparkle"
              className="proactive-ai-prompt__icon"
              aria-hidden="true"
            />

            <div className="proactive-ai-prompt__textarea-container">
              {/* Label is visually hidden but remains for a11y */}
              <label htmlFor={inputId} className="sr-only">
                {trimmedPlaceholder}
              </label>

              <textarea
                ref={textareaRef}
                id={inputId}
                name="proactive-ai-prompt"
                placeholder={isSmall ? smallPlaceholder : effectivePlaceholder}
                className="proactive-ai-prompt__input"
                rows={1}
                autoFocus
                value={value}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                aria-describedby={instructionsId}
              />

              <div id={instructionsId} className="sr-only">
                {t("js.proactive_ai_prompt.instructions")}
              </div>
            </div>

            <div
              className={cn(
                "proactive-ai-prompt__bottom-row",
                isMultiline &&
                  isSmall &&
                  "proactive-ai-prompt__bottom-row--with-label",
              )}
            >
              {isMultiline && isSmall && (
                <div className="proactive-ai-prompt__powered-by">
                  <Icon
                    icon="sparkle"
                    size="sm"
                    className="proactive-ai-prompt__powered-by-icon"
                    aria-hidden="true"
                  />
                  <span className="proactive-ai-prompt__powered-by-text">
                    {t("js.proactive_ai_prompt.powered_by_ai")}
                  </span>
                </div>
              )}

              <div className="proactive-ai-prompt__buttons">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      iconOnly
                      size={isSmall ? "sm" : undefined}
                      className="proactive-ai-prompt__button proactive-ai-prompt__button--help"
                      type="button"
                      aria-label={t("js.proactive_ai_prompt.help")}
                    >
                      <Icon
                        icon="question-circle"
                        size={isSmall ? "sm" : undefined}
                        aria-hidden="true"
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("js.proactive_ai_prompt.help_tooltip")}
                  </TooltipContent>
                </Tooltip>
                <Button
                  variant="primary"
                  iconOnly
                  size={isSmall ? "sm" : undefined}
                  className="proactive-ai-prompt__button"
                  type="submit"
                  disabled={isDisabled}
                  aria-label={
                    isLoading
                      ? t("js.proactive_ai_prompt.processing")
                      : t("js.proactive_ai_prompt.submit")
                  }
                >
                  <Icon
                    icon="arrow-up"
                    size="sm"
                    variant="solid"
                    loading={isLoading}
                    aria-hidden="true"
                  />
                </Button>
              </div>
            </div>
          </form>

          {isLoading && (
            <div role="status" aria-live="polite" className="sr-only">
              {t("js.proactive_ai_prompt.loading_status")}
            </div>
          )}
        </div>
      </TooltipProvider>
    )
  },
)

ProactiveAiPrompt.displayName = "ProactiveAiPrompt"

export default ProactiveAiPrompt

/*
 * Notes on CSS (not included):
 *  - Keep your existing class names. Transition classes can drive transform/translate and opacity.
 *  - The autosize hook only manipulates inline height on the <textarea>.
 *  - Ensure .proactive-ai-prompt__field--multiline updates layout (padding, min-height) as needed.
 */
